import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { Field } from '@/components/live';
import { callRpc } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';

/**
 * The approval PIN (owner, 2026-09-21: "for approvals, set pin"). Every approve / reject /
 * return, lock and unlock asks for it; the server checks it (migration 0065) and answers a wrong
 * one with one of these codes rather than an error, so five wrong tries really do lock it.
 */
export const PIN_RESULTS: Record<string, string> = {
  pin_not_set: 'Set your approval PIN first.',
  pin_incorrect: 'That PIN is not right.',
  pin_locked: 'Too many wrong PINs. Your PIN is locked for 15 minutes.',
};

/** True when the server's answer was about the PIN rather than the decision. */
export function isPinResult(outcome: string | null | undefined): boolean {
  return Boolean(outcome && outcome in PIN_RESULTS);
}

export function useHasApprovalPin() {
  return useQuery({
    queryKey: ['approval-pin'],
    queryFn: () => callRpc<boolean>('fn_has_approval_pin', {}),
    enabled: isSupabaseConfigured,
  });
}

/**
 * The PIN box used wherever something is decided. With no PIN set yet it offers to set one
 * there and then, so the first approval is not a dead end.
 */
export function ApprovalPinField({ value, onChange, error }: { value: string; onChange: (pin: string) => void; error?: string }) {
  const hasPin = useHasApprovalPin();
  const [changing, setChanging] = useState(false);

  if (hasPin.data === false || changing) {
    return <ApprovalPinSetup changing={changing && hasPin.data === true} onDone={() => setChanging(false)} />;
  }
  return (
    <Field error={error} label="Your approval PIN" required>
      <div className="pin-row">
        <input autoComplete="off" className="pin-input" inputMode="numeric" maxLength={6}
               onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
               placeholder="••••" type="password" value={value} />
        <button className="dg-link" onClick={() => setChanging(true)} type="button">Change PIN</button>
      </div>
    </Field>
  );
}

export function ApprovalPinSetup({ changing, onDone }: { changing?: boolean; onDone?: () => void }) {
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState('');
  const [pin, setPin] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const digits = (value: string) => value.replace(/\D/g, '').slice(0, 6);

  async function save() {
    setError(undefined);
    if (!/^\d{4,6}$/.test(pin)) { setError('The PIN must be 4 to 6 digits.'); return; }
    if (pin !== again) { setError('The two PINs do not match.'); return; }
    setBusy(true);
    try {
      const outcome = await callRpc<string>('fn_set_approval_pin', { p_new_pin: pin, p_current_pin: changing ? current : null });
      if (outcome !== 'ok') { setError(PIN_RESULTS[outcome] ?? outcome); return; }
      toast.success(changing ? 'Approval PIN changed.' : 'Approval PIN set.');
      await queryClient.invalidateQueries({ queryKey: ['approval-pin'] });
      onDone?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The PIN could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pin-setup">
      <p className="muted" style={{ margin: 0 }}>
        <KeyRound size={14} /> {changing ? 'Change your approval PIN.' : 'Approvals, locks and unlocks need a PIN of your own. Set it once; it is asked for every decision.'}
      </p>
      <div className="field-row">
        {changing && (
          <Field label="Current PIN">
            <input className="pin-input" inputMode="numeric" onChange={(event) => setCurrent(digits(event.target.value))} type="password" value={current} />
          </Field>
        )}
        <Field hint="4 to 6 digits. Not 1234 or 0000." label="New PIN">
          <input className="pin-input" inputMode="numeric" onChange={(event) => setPin(digits(event.target.value))} type="password" value={pin} />
        </Field>
        <Field label="New PIN again">
          <input className="pin-input" inputMode="numeric" onChange={(event) => setAgain(digits(event.target.value))} type="password" value={again} />
        </Field>
      </div>
      {error && <p className="login-error" role="alert">{error}</p>}
      <div className="inline-form-actions">
        {changing && <button className="cancel" onClick={onDone} type="button">Cancel</button>}
        <button className="primary" disabled={busy} onClick={() => void save()} type="button">{busy ? 'Saving...' : 'Save PIN'}</button>
      </div>
    </div>
  );
}
